import { Checkbox, Grid } from "@mui/material";

import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { MEMBER_SELECTION_PAGE_SIZE, PlanAssigmentLimit, includeAboveProperty } from "utils/CommonConstants";
import {
  getHltInncodes,
  getMoreValues,
  getPropertyName,
  getUserTypeTag,
  getUserTypeValue
} from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import BubbleToggle from "../BubbleToggle";
import {
  fetchMembersAsync,
  handleUndelectedMember,
  setIsAllMemberSelected,
  setIsErrorWhenLimitExceed,
  setPayloadData,
  setTotalMemberSelection
} from "../PlanAssignment.slice";
import SelectedList from "../SelectedList";
import Filters from "./Filters";
import "./index.scss";
import { assignCopyHeaders, headers } from "./TableHeaders";

const MemberSelection = (props: any) => {
  const dispatch = useAppDispatch();

  const { inncodes, isAssignCopy, isPlanProgress } = props;

  const {
    memberSelection: { totalList, list },
    payloadData: { users, plans }
  } = useAppSelector((state) => state.PlanAssignment);
  const filters = useAppSelector((state: any) => state.PlanAssignment.memberSelection.filters);
  const { brand, region } = useAppSelector((state) => state.PlanAssignment.planSelection.filters);
  const isAllMemberSelected = useAppSelector((state) => state.PlanAssignment.isAllMemberSelected);
  const unselecteMember = useAppSelector((state) => state.PlanAssignment.unselecteMember);
  const totalMemberSelection = useAppSelector((state) => state.PlanAssignment.totalMemberSelection);
  const [pageNo, setPageNo] = useState(0);
  const [checkedList, setCheckedList] = useState<any>(users);
  const [totalUser, setTotalUser] = useState<number>(0);
  const [isMount, setIsMount] = useState<boolean>(false);

  const getMembersAPIPayload = (page?: any) => {
    const meta = isPlanProgress ? props?.plans?.[0]?.meta : {};
    return {
      searchText: filters.searchText,
      innCodes: inncodes?.join(",") || filters?.property?.join(",") || getHltInncodes(true),
      page: page ?? pageNo,
      size: MEMBER_SELECTION_PAGE_SIZE,
      roles: filters.role?.join(","),
      userType: getUserTypeValue(filters[includeAboveProperty]),
      ignoreNonFilteredInnCodes: true,
      respondWithHltIdAndInnCode: true,
      includecorpusers: false,
      ...(meta?.brandCodes?.length ? { brandCodes: meta.brandCodes } : brand?.length ? { brandCodes: brand } : {}),
      ...(meta?.regions?.length
        ? { regions: meta.regions?.join("::") }
        : region?.length
        ? { regions: region?.join("::") }
        : {}),
      ...(meta?.locationTypes?.length ? { locationTypes: meta.locationTypes } : {}),
      ...(meta?.innCodes?.length ? { tgInnCodes: meta.innCodes } : {}),
      ...(meta?.managementTypes?.length ? { mgmtTypes: meta.managementTypes } : {})
    };
  };

  useEffect(() => {
    (async () => {
      setPageNo(0);
      const payload = getMembersAPIPayload(0);
      const res = await dispatch(fetchMembersAsync(payload));
      const totalUser = res?.payload?.meta?.page?.total;
      setTotalUser(totalUser);
      if (isMount && isAllMemberSelected) {
        setCheckedList([]);
        dispatch(setPayloadData({ users: [] }));
        dispatch(setIsAllMemberSelected(false));
        dispatch(handleUndelectedMember([]));
      }
    })();
  }, [filters]);

  useEffect(() => {
    setIsMount(true);
  }, []);

  useEffect(() => {
    // Calculate how many members are actually selected
    // If "select all" is active → total members minus unselected members
    // Otherwise → only the manually checked members count
    const memberCount = isAllMemberSelected
      ? totalMemberSelection - (unselecteMember?.length ?? 0)
      : checkedList.length;

    const isError = memberCount * plans.length > PlanAssigmentLimit;

    // Update error state in redux
    dispatch(setIsErrorWhenLimitExceed(isError));
  }, [isAllMemberSelected, totalMemberSelection, unselecteMember?.length, checkedList.length, plans.length, dispatch]);

  useEffect(() => {
    const payload = getMembersAPIPayload();
    if (pageNo > 0) dispatch(fetchMembersAsync(payload));
  }, [pageNo]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, element: any) => {
    let arr = [...checkedList];
    if (event.target?.checked) {
      arr.push(element);
      handleUnselected(true, element);
    } else {
      arr = arr.filter((el: any) => {
        if (!(el?.hltId === element?.hltId && el.innCodes[0] === element?.innCodes[0])) return el;
      });
      handleUnselected(false, element);
    }
    setCheckedList(arr);
    dispatch(setPayloadData({ users: arr }));
  };

  const handleUnselected = (isSelected: boolean, member: any) => {
    if (isAllMemberSelected) {
      if (isSelected) {
        const filtered = unselecteMember.filter(
          (item: any) => !(item.innCodes?.[0] === member.innCodes[0] && item.hltId === member?.hltId)
        );
        dispatch(handleUndelectedMember(filtered));
      } else {
        const unselected = [...unselecteMember];
        unselected.push(member);
        dispatch(handleUndelectedMember(unselected));
      }
    }
  };

  const selectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked) {
      setCheckedList([]);
      dispatch(setPayloadData({ users: [] }));
    } else {
      dispatch(setTotalMemberSelection(totalUser));
    }
    dispatch(setIsAllMemberSelected(event.target.checked));
    dispatch(handleUndelectedMember([]));
  };

  useEffect(() => {
    if (isAllMemberSelected) {
      const result = list.filter((item1: any) => {
        const isDuplicate = unselecteMember.some(
          (item2: any) => item2.hltId === item1.hltId && item2.innCodes[0] === item1.innCodes[0]
        );
        return !isDuplicate;
      });
      setCheckedList(result);
      dispatch(setPayloadData({ users: result }));
    }
  }, [list, isAllMemberSelected, unselecteMember]);

  const getRows = () => {
    return list?.map((element: any) => {
      const tableRow = {
        data: [
          {
            content: (
              <Grid display="flex" flexDirection={"row"} width="180px">
                <Checkbox
                  onChange={(event: any) => handleCheckboxChange(event, element)}
                  checked={
                    checkedList?.findIndex(
                      (el: any) => el.hltId === element.hltId && el.innCodes[0] === element.innCodes[0]
                    ) > -1
                  }
                />

                <Typography sx={{ marginTop: "8px" }}>
                  {" "}
                  {getUserNameWithConditionalCheck(element?.displayName, element?.firstName, element?.lastName)}
                </Typography>
              </Grid>
            ),
            isCheckboxHidden: true,
            value: getUserNameWithConditionalCheck(element?.displayName, element?.firstName, element?.lastName) || ""
          },

          {
            content: getMoreValues(element?.roles),
            tooltip: {
              title: "Role",
              list: element?.roles?.map((owner: any) => {
                return { name: owner };
              })
            }
          },
          {
            content: getUserTypeTag(element?.lobbyUserType)
          }
        ]
      };
      return isAssignCopy
        ? tableRow
        : {
            ...tableRow,
            data: [
              ...tableRow.data,
              { content: getPropertyName(element?.innCodes?.[0]) },
              { content: element?.innCodes?.[0] }
            ]
          };
    });
  };

  return (
    <div className="member_selection">
      <div className="member_selection-heading">
        <div>
          <Typography variant="h5" color={tokens.Text.General.PrimaryHeadline}>
            Select one or more Plan Members.
          </Typography>
        </div>
        <BubbleToggle />
      </div>
      <div>
        <Filters showPropertyFilter={inncodes ? inncodes.length > 1 : true} isPreDefinedNav={true} />
        {!isAllMemberSelected && (
          <SelectedList checkedList={checkedList} setCheckedList={handleCheckboxChange} type={"Users"} />
        )}
        <div className="member_selection-table">
          <BasicTable
            scrollableTarget="right-wrapper-container"
            pagination={{ pageNo, setPageNo }}
            totalRecords={totalList}
            dataLength={list?.length}
            handleSelectedAll={selectAll}
            selectAllProperties={isAllMemberSelected}
            data={{
              headers: isAssignCopy ? assignCopyHeaders : headers,
              rows: getRows()
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MemberSelection;
